/**
 * TELC C1 Hochschule blog post: Redemittel Schreiben & Sprechen (PDF).
 * Content: intro, exam structure table, oral/written structure, blockquote, vocabulary, simulators, FAQ.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'How long does the c1 hochschule exam take?',
    answer:
      'The written exam takes 3 hours 40 minutes including the break: 90 minutes for Leseverstehen and Sprachbausteine together, 40 minutes for Hörverstehen and 70 minutes for the Schriftlicher Ausdruck. The oral exam is held separately and lasts about 16 minutes, preceded by 20 minutes of preparation time.',
  },
  {
    question: 'How many writing tasks does telc C1 Hochschule have?',
    answer:
      'One. You choose one of two given topics and write a single argumentative text of at least 350 words in 70 minutes. There is no separate obligatory email and no second writing task, so the Redemittel you need are essay and Stellungnahme phrases.',
  },
  {
    question: 'Where can I download the Redemittel PDF?',
    answer:
      'You can download the complete telc deutsch c1 Redemittel PDF directly from the logami.app resource hub. It includes all necessary academic phrases for the written and oral sections.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const TelcC1RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        To secure the <strong>telc deutsch c1</strong> certification, candidates must execute precise academic phrasing. This definitive guide provides the exact Redemittel for the <strong>written and oral part</strong>, essential study tips, and preparation material as a free PDF download. Ready-made academic phrasing frees up the attention you would otherwise spend searching for formulations, so you can put it into argument and structure &mdash; the two things telc raters weight most heavily.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        The telc C1 Hochschule exam at a glance
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Before you learn a single phrase, know where they have to land. These are the five subtests,
        their timings and their weight in the final score:
      </p>

      <div className="overflow-x-auto my-8 rounded-2xl border-2 border-black/10 bg-white shadow-sm">
        <table className="w-full min-w-[480px] text-left text-sm">
          <thead>
            <tr className="border-b-2 border-black/10 bg-black/5">
              <th className="px-4 py-3 font-space font-bold text-black">Subtest</th>
              <th className="px-4 py-3 font-space font-bold text-black">Time</th>
              <th className="px-4 py-3 font-space font-bold text-black">Points</th>
              <th className="px-4 py-3 font-space font-bold text-black">Weight</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 text-black/70">Leseverstehen</td>
              <td className="px-4 py-3 text-black/60">90 min (shared with Sprachbausteine)</td>
              <td className="px-4 py-3 text-black/60">48</td>
              <td className="px-4 py-3 text-black/60">22.5%</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 text-black/70">Sprachbausteine</td>
              <td className="px-4 py-3 text-black/60">within the same 90 min</td>
              <td className="px-4 py-3 text-black/60">22</td>
              <td className="px-4 py-3 text-black/60">10%</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 text-black/70">Hörverstehen</td>
              <td className="px-4 py-3 text-black/60">40 min</td>
              <td className="px-4 py-3 text-black/60">48</td>
              <td className="px-4 py-3 text-black/60">22.5%</td>
            </tr>
            <tr className="bg-black/5 hover:bg-black/[0.07]">
              <td className="px-4 py-3 font-medium text-black">Schriftlicher Ausdruck</td>
              <td className="px-4 py-3 font-semibold text-black">70 min &mdash; one task</td>
              <td className="px-4 py-3 font-semibold text-black">48</td>
              <td className="px-4 py-3 font-semibold text-black">22.5%</td>
            </tr>
            <tr className="hover:bg-black/[0.03]">
              <td className="px-4 py-3 text-black/70">Mündlicher Ausdruck</td>
              <td className="px-4 py-3 text-black/60">~16 min, after 20 min preparation</td>
              <td className="px-4 py-3 text-black/60">48</td>
              <td className="px-4 py-3 text-black/60">22.5%</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        You need 60% overall to pass, and the written and oral parts are scored separately &mdash; a strong
        essay cannot rescue a weak presentation, and the reverse is equally true.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        How Does the Oral Part Structure Dictate Redemittel Usage?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The <strong>oral part</strong> of the <strong>c1 exam</strong> asks you to present, summarize and then defend a position in discussion. You get <strong>20 minutes of preparation</strong> and roughly <strong>16 minutes</strong> in front of the examiners &mdash; too little time to invent phrasing on the spot, which is exactly why a rehearsed inventory of Redemittel pays off here.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The <strong>written part</strong> runs <strong>3 hours 40 minutes</strong> including the break, and closes with the <strong>Schriftlicher Ausdruck: 70 minutes for one text of at least 350 words</strong>. Across both parts you have to articulate complex arguments, hypothesize, and structure formal discourse. Merely understanding the <strong>deutsch c1 hochschule</strong> vocabulary is insufficient; you must deploy it under strict time constraints.
      </p>

      <blockquote className="border-l-4 border-black bg-black/5 pl-6 pr-4 py-4 my-8 rounded-r-xl text-black/80 italic">
        <p className="text-base md:text-lg mb-2">
          &bdquo;Wählen Sie eines der folgenden zwei Themen. Schreiben Sie einen Text, in dem Sie Ihren
          eigenen Standpunkt dazu erarbeiten und argumentativ darlegen. Ihr Text soll mindestens 350
          Wörter umfassen. Sie haben 70 Minuten Zeit.&ldquo;
        </p>
        <p className="text-sm not-italic text-black/60">
          The Schriftlicher Ausdruck instruction, as it appears in the exam. Every phrase in this guide
          exists to serve those three demands: take a position, argue it, and finish inside 70 minutes.
        </p>
      </blockquote>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Core Vocabulary Execution for the Written Part
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Executing the <strong>written part</strong> demands a robust vocabulary matrix. Candidates must integrate advanced connectors, academic verbs, and precise nouns to satisfy the grading rubric and secure the necessary points for the <strong>telc deutsch</strong> certificate.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        Notes and generic textbooks rarely prepare you for the time pressure of the <strong>telc c1</strong>. Logami is built by German teachers and has 12 complete telc C1 Hochschule mock exams in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Why Are Authentic Exam Simulators Critical for Success?
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        Authentic exam simulators replicate the exact pressure of the actual <strong>telc exam</strong>. Training under timed conditions optimizes your cognitive reflexes, ensuring you process information rapidly and apply your <strong>language skills</strong> without hesitation during the official test.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        The <strong>c1 hochschule</strong> exam demands rigorous time management. The interface runs in six languages (English, German, French, Spanish, Turkish and Russian), so the grammar explanations land in the language you think in. Practicing against the clock is what makes the timings survivable: <strong>90 minutes</strong> for Leseverstehen and Sprachbausteine together, <strong>40 minutes</strong> for Hörverstehen, and <strong>70 minutes</strong> for the essay.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          For the complete C1 Academic Redemittel collection.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full TELC C1 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="telc"
        level="c1"
        examName="telc C1"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> September 2, 2026
      </p>
      <SprechenSamplesCallout provider="telc" level="c1" />
      <RelatedResources provider="telc" level="c1" variant="redemittel" />

    </article>
  );
};

export default TelcC1RedemittelPost;
